import React, { useState } from 'react';

import { path } from 'ramda';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { findIndexFromFieldsArray } from 'components/shared/CRUD/utils/utils';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPage';
import withFieldArrayDrawer from 'components/shared/CRUD/components/InnerPage/components/withFieldArrayDrawer';
import { fieldArrayContext } from 'components/shared/CRUD/components/InnerPage/hooks/useFieldArrayContext';
import useRemoveUncommitted from 'components/shared/CRUD/components/InnerPage/hooks/useRemoveUncommitted';

import useDetailsColumnDefinitions from '../../hooks/useDetailsColumnDefinitions';
import EditDetailsLine from './editDetailsLines/EditDetailsLine';

interface Props {
  setDeletedIds: React.Dispatch<React.SetStateAction<number[]>>;
}

const EditDetailsLines = withFieldArrayDrawer(EditDetailsLine, 'details');

const SurchargesMaintenanceDetailsTable: React.FC<Props> = ({
  setDeletedIds
}) => {
  const { isEditable } = useEditPageContext();
  const { t } = useTranslation('common');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const columns = useDetailsColumnDefinitions();

  const { control, getValues } = useFormContext();

  const {
    fields: details,
    append,
    remove,
    update,
    replace
  } = useFieldArray({
    control: control,
    name: 'details'
  });

  const onAddNew = () => {
    // add a new empty record to the fieldArray
    append({
      isCommitted: false,
      valid: { from: null, to: null },
      flags: ['SERVICE_BR_DEFAULT_FLAG'],
      surcharge1Perc: 0,
      surcharge3Perc: 0,
      surchargeId: getValues('surchargeId')
    });
    setIsDrawerOpen(true);
  };

  const onDelete = (fieldIds) => {
    const index = findIndexFromFieldsArray(fieldIds[0], details);
    const id = path<number>(['surchargeDynId'], details[index]);
    if (id) {
      setDeletedIds((prevState) => [...prevState, id]);
    }
    remove(index);
  };

  useRemoveUncommitted(details, isDrawerOpen, remove);

  const onCloseInnerView = () => {
    setIsDrawerOpen(false);
  };

  return (
    <fieldArrayContext.Provider
      value={{
        fields: details,
        append,
        remove,
        update,
        replace
      }}
    >
      <TableCRUDLayout
        columns={columns}
        rows={details}
        FormComponent={EditDetailsLines}
        getRowId={(row) => row.id}
        autoHeight={true}
        disableActions={!isEditable}
        onAddClick={onAddNew}
        onDeleteClick={onDelete}
        confirmationTitle={t('surchargesMaintenance.delete.title')}
        confirmationBody={t('surchargesMaintenance.delete.body')}
        onClose={onCloseInnerView}
        shouldRenderToolBar={false}
      />
    </fieldArrayContext.Provider>
  );
};

export default SurchargesMaintenanceDetailsTable;
